import React, { useContext, useState, useMemo, useCallback, useEffect } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { notificationApi } from '@bingads-webui-react/notification-center';
import { StepContainer } from '@bingads-webui-campaign-react/goal-creation-wizard';
import { SharedLibraryUtils } from '@bingads-webui-campaign-react/shared-library-scopes';
import { UetSetupWizardContext } from '../../uet-setup-wizard-context';
import { CreateUetTagView } from './create-uet-tag-view';
import { WIZARD_ACTIONS } from '../../uet-setup-wizard-reducer';
import { getErrorMessage, saveTag } from '../../uet-tag-repository';

export const isAccountLevel = scope => (scope.values && scope.values.aid);

export const CreateUetTagStep = (props) => {
  const {
    legacyI18n,
    permissions,
    customerHierarchy,
  } = props;
  const isTagSharingScope = !_.isEmpty(customerHierarchy);
  const i18n = useContext(I18nContext);
  const wizardContext = useContext(UetSetupWizardContext);
  const {
    scope,
    serverContext,
    tagContext,
  } = wizardContext;

  const createTagTitle = i18n.getString(_TL_('Create UET tag'));
  const editTagTitle = i18n.getString(_TL_('Edit UET tag'));

  const [tagEntity, setTagEntity] = useState(tagContext);
  const [showError, setShowError] = useState(false);
  const [notificationId, setNotificationId] = useState(null);

  useEffect(() => () => notificationId && notificationApi.dismiss(notificationId), [notificationId]);

  const showNotification = useCallback((message) => {
    setNotificationId(notificationApi.showWarning(message));
  }, []);

  const resetShowError = useCallback(() => setShowError(false), []);

  const updateTagEntity = useCallback((updatedTag) => {
    setTagEntity({
      ...tagEntity,
      ...updatedTag,
    });
  }, [tagEntity]);

  const tagOwner = useMemo(() => {
    const { CurrentAccount: currentAccount, CurrentCustomer: currentCustomer } = serverContext;
    let owner = _.pick(currentCustomer, ['Id', 'Name', 'Number']);
    if (isAccountLevel(scope)) {
      const { AdvertiserCustomerID: parentCid } = currentAccount || {};
      // TOTO from xuwu: we need also populate the Name and Number of the advertiserCustomer
      owner = !_.isFinite(parentCid) || parentCid === currentCustomer.Id ? owner : { Id: parentCid };
    }
    return owner;
  }, [scope, serverContext]);

  const saveAndNextHandler = useCallback(() => {
    // validate name
    if (_.isEmpty(tagEntity.Name)) {
      setShowError(true);
      return;
    }
    saveTag(_.defaults(tagEntity, isTagSharingScope ? { Owner: tagOwner } : null), scope, serverContext)
      .then((response) => {
        if (response.Id) {
          const tagId = response.Id;
          if (tagId) {
            wizardContext.dispatch({
              type: WIZARD_ACTIONS.SAVE_TAG_AND_NEXT,
              data: { ...tagEntity, Id: tagId },
            });
          }
        }
      })
      .catch((response) => {
        if (response.body.value && response.body.value.length > 0) {
          showNotification(getErrorMessage(response.body.value[0].Code, i18n));
        } else {
          showNotification(i18n.getString(_TL_('We\'ve encountered an error while saving this tag. Please try again or contact support for assistance.')));
        }
      });
  }, [i18n, isTagSharingScope, scope, serverContext, tagEntity, tagOwner, wizardContext, showNotification]);

  const footerConfig = useMemo(() => ({
    buttons: [{
      type: 'Primary-Start',
      onClick: saveAndNextHandler,
      isEnabled: true,
      text: i18n.getString(_TL_('Save and next')),
    },
    {
      type: 'Cancel',
      onClick: wizardContext.exitWizard,
      isEnabled: true,
      text: i18n.getString(_TL_('Cancel')),
    }],
  }), [i18n, saveAndNextHandler, wizardContext]);

  return (
    <StepContainer
      title={tagContext.isEdit ? editTagTitle : createTagTitle}
      step="create-uet-tag"
      footerConfig={footerConfig}
    >
      <CreateUetTagView
        i18n={i18n}
        legacyI18n={legacyI18n}
        permissions={permissions}
        serverContext={serverContext}
        tagEntity={tagEntity}
        tagSharingScopeContext={{
          isTagSharingScope,
          selectedSharingOption: tagEntity.selectedSharingOption || SharedLibraryUtils.Scopes.CUSTOMER,
          customerHierarchy,
          tagOwner,
          sharingScope: tagEntity.SharingScope || {
            Customers: [],
            Accounts: [],
            Others: 0,
            Total: 0,
          },
        }}
        updateTagEntity={updateTagEntity}
        showError={showError}
        resetShowError={resetShowError}
      />
    </StepContainer>
  );
};

CreateUetTagStep.propTypes = {
  legacyI18n: PropTypes.objectOf(PropTypes.any).isRequired,
  permissions: PropTypes.objectOf(PropTypes.any).isRequired,
  customerHierarchy: PropTypes.arrayOf(PropTypes.any),
};

CreateUetTagStep.defaultProps = {
  customerHierarchy: null,
};
